import { useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '@/services/adminApi';

/**
 * Admin Config Page
 * 관리자 설정 페이지
 */
const ConfigPage = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [editingConfig, setEditingConfig] = useState<any>(null);
  const [formData, setFormData] = useState({ key: '', value: '', description: '' });
  const queryClient = useQueryClient();

  const { data: configsData, isLoading, isError, error } = useQuery({
    queryKey: ['admin', 'configs'],
    queryFn: () => adminApi.getConfigs(),
    retry: 1,
  });

  const createMutation = useMutation({
    mutationFn: (data: any) => adminApi.createConfig(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'configs'] });
      setOpenDialog(false);
      setFormData({ key: '', value: '', description: '' });
    },
    onError: (error: any) => {
      console.error('Failed to create config:', error);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ key, data }: { key: string; data: any }) => adminApi.updateConfig(key, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'configs'] });
      setOpenDialog(false);
      setEditingConfig(null);
      setFormData({ key: '', value: '', description: '' });
    },
    onError: (error: any) => {
      console.error('Failed to update config:', error);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (key: string) => adminApi.deleteConfig(key),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'configs'] });
    },
    onError: (error: any) => {
      console.error('Failed to delete config:', error);
    },
  });

  // 백엔드는 ApiResponseDto로 응답하므로 data.data가 배열
  const configs = Array.isArray(configsData?.data) ? configsData.data : [];

  const handleOpenCreate = () => {
    setEditingConfig(null);
    setFormData({ key: '', value: '', description: '' });
    setOpenDialog(true);
  };

  const handleOpenEdit = (config: any) => {
    setEditingConfig(config);
    setFormData({
      key: config.key,
      value: config.value,
      description: config.description || '',
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingConfig(null);
    setFormData({ key: '', value: '', description: '' });
  };

  const handleSubmit = () => {
    if (editingConfig) {
      updateMutation.mutate({
        key: editingConfig.key,
        data: { value: formData.value, description: formData.description },
      });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleDelete = (key: string) => {
    if (window.confirm(`설정 "${key}"을(를) 삭제하시겠습니까?`)) {
      deleteMutation.mutate(key);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">설정 관리</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
          설정 추가
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>키</TableCell>
              <TableCell>값</TableCell>
              <TableCell>설명</TableCell>
              <TableCell align="right">작업</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  로딩 중...
                </TableCell>
              </TableRow>
            ) : isError ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Alert severity="error" sx={{ mb: 0 }}>
                    설정을 불러오는데 실패했습니다.{' '}
                    {error instanceof Error ? error.message : '알 수 없는 오류가 발생했습니다.'}
                  </Alert>
                </TableCell>
              </TableRow>
            ) : configs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  설정이 없습니다.
                </TableCell>
              </TableRow>
            ) : (
              configs.map((config: any) => (
                <TableRow key={config.id || config.key}>
                  <TableCell>{config.key}</TableCell>
                  <TableCell>{config.value}</TableCell>
                  <TableCell>{config.description || '-'}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleOpenEdit(config)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(config.key)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingConfig ? '설정 수정' : '설정 추가'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="키"
              value={formData.key}
              onChange={(e) => setFormData({ ...formData, key: e.target.value })}
              disabled={!!editingConfig}
              required
            />
            <TextField
              label="값"
              value={formData.value}
              onChange={(e) => setFormData({ ...formData, value: e.target.value })}
              required
            />
            <TextField
              label="설명"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              rows={3}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>취소</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingConfig ? '수정' : '추가'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ConfigPage;
